/**
 * The decisions behind the portal-grants drawer, separated from its markup.
 *
 * Three of them are load-bearing and none is visible in a rendered checkbox:
 * which sites the list is allowed to omit, what the operator is told they are
 * about to do, and when `confirmEmpty` is sent. They live here so they can be
 * driven directly, the way `asset-form.ts` is.
 */

export interface SiteRef {
  id: string;
  code: string | null;
  name: string;
}

/** A site as offered in the list, with why it is here. */
export interface Option extends SiteRef {
  /** Granted, but absent from the active site list. See `mergeOptions`. */
  retired: boolean;
}

export function label(site: SiteRef): string {
  return site.code === null ? site.name : `${site.code} — ${site.name}`;
}

/**
 * The list to draw: active sites, UNION whatever is currently granted.
 *
 * 🔴 `GET /api/sites` excludes retired sites. A client granted a site that was
 * later retired therefore holds a grant the drawer would not draw — and because
 * `PUT /api/users/:id/sites` REPLACES the set, saving from the drawn checkboxes
 * alone would drop it without anyone choosing to.
 *
 * That is the silently-shortened set the route refuses unknown ids to prevent,
 * arriving through the front door instead: the route can only refuse ids it is
 * SENT, and this one would simply be missing. The union is the fix, and the
 * `retired` flag is what lets the drawer say why the row looks odd rather than
 * leaving somebody to wonder.
 */
export function mergeOptions(granted: readonly SiteRef[], active: readonly SiteRef[]): Option[] {
  const activeIds = new Set(active.map((s) => s.id));
  const merged: Option[] = [
    ...active.map((s) => ({ ...s, retired: false })),
    ...granted.filter((s) => !activeIds.has(s.id)).map((s) => ({ ...s, retired: true })),
  ];
  merged.sort((a, b) => (a.code ?? a.name).localeCompare(b.code ?? b.name));
  return merged;
}

export interface Change {
  adding: string[];
  removing: string[];
}

/**
 * What is about to happen, by name.
 *
 * 🔴 The screen's actual subject. The predecessor's empty-ACL incident is
 * recorded as "nobody had decided to remove their access — the form had simply
 * submitted what it was showing", and no confirmation on the DESTINATION can
 * catch that: a form showing the wrong set will confirm the wrong set happily.
 * Naming the removals is what gives an operator something to disagree with.
 *
 * Names, not ids, because "removing CK1048 — Riverside Depot" is a sentence
 * somebody can check against what they meant to do and a uuid is not.
 */
export function diff(
  saved: ReadonlySet<string>,
  picked: ReadonlySet<string>,
  options: readonly Option[],
): Change {
  const byId = new Map(options.map((o) => [o.id, o]));
  // An id with no option is named as itself rather than dropped. It should not
  // happen, and a diff that quietly omits a row is the failure being guarded.
  const name = (id: string): string => {
    const found = byId.get(id);
    return found === undefined ? id : label(found);
  };
  return {
    adding: [...picked].filter((id) => !saved.has(id)).map(name).sort(),
    removing: [...saved].filter((id) => !picked.has(id)).map(name).sort(),
  };
}

export function isDirty(change: Change): boolean {
  return change.adding.length > 0 || change.removing.length > 0;
}

/**
 * The one-line heading over the diff.
 *
 * Built here rather than assembled from conditional fragments in JSX, which is
 * how it first shipped and how it first read "taking away 1" with a lowercase
 * t — the clause was written assuming "Granting n" would always precede it, and
 * a removal-only edit is the most important case on this screen.
 */
export function headline(change: Change): string {
  const parts: string[] = [];
  if (change.adding.length > 0) parts.push(`Granting ${change.adding.length}`);
  if (change.removing.length > 0) parts.push(`taking away ${change.removing.length}`);
  const sentence = parts.join(', ');
  return sentence === '' ? '' : sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

export interface GrantsBody {
  siteIds: string[];
  confirmEmpty?: true;
}

/**
 * The request body.
 *
 * `confirmEmpty` is sent only for a genuinely empty set, and it is derived here
 * rather than held in state — a flag a component can leave set after the
 * operator ticks a box again is a flag that eventually confirms something
 * nobody was asked about.
 */
export function bodyFor(picked: ReadonlySet<string>): GrantsBody {
  const siteIds = [...picked];
  return siteIds.length === 0 ? { siteIds, confirmEmpty: true } : { siteIds };
}
